import React, { useEffect, useState, useCallback } from 'react';
import API from '../api';
import DMChat from './DMChat';

/**
 * Modal overlay that shows a single user's stats & badges, plus a Messages tab for 1:1 DMs.
 * Props:
 *   userId   — numeric id to load
 *   me       — current user ({ id, username, is_admin })
 *   onClose  — callback when user dismisses the modal
 *   initialTab — 'stats' (default) or 'messages'
 */
export default function UserStatsModal({ userId, me, onClose, initialTab = 'stats' }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await API.get(`/picks/user-stats/${userId}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not load stats');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!userId) return null;

  const pctColor = (pct) => {
    if (pct == null) return '#7fb3ff';
    if (pct >= 70) return '#4caf50';
    if (pct >= 55) return '#8bc34a';
    if (pct >= 45) return '#ffa726';
    return '#ef5350';
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'userStatsFade 0.15s ease'
      }}
    >
      <style>{`
        @keyframes userStatsFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes userStatsPop  { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0d1526',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 14,
          maxWidth: 520,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          animation: 'userStatsPop 0.18s ease'
        }}
      >
        {/* Header */}
        <div style={{
          position: 'sticky', top: 0,
          padding: '18px 22px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'linear-gradient(135deg, rgba(1,51,105,0.5), rgba(10,14,26,0.9))',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderTopLeftRadius: 14, borderTopRightRadius: 14
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#7fb3ff', letterSpacing: 2, fontFamily: "'Oswald', sans-serif", fontWeight: 700, textTransform: 'uppercase' }}>
              Player Stats
            </div>
            <div style={{ fontSize: 22, fontFamily: "'Oswald', sans-serif", fontWeight: 700, color: '#fff', marginTop: 2 }}>
              {data?.user?.username || (loading ? '…' : 'Unknown')}
              {data?.user?.is_admin && (
                <span style={{
                  marginLeft: 8, fontSize: 10, padding: '3px 8px',
                  background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)',
                  color: '#FFD700', borderRadius: 10, verticalAlign: 'middle', letterSpacing: 0.5
                }}>⭐ ADMIN</span>
              )}
              {data?.user && !data.user.is_active && (
                <span style={{
                  marginLeft: 8, fontSize: 10, padding: '3px 8px',
                  background: 'rgba(213,10,10,0.15)', border: '1px solid rgba(213,10,10,0.3)',
                  color: '#ff8888', borderRadius: 10, verticalAlign: 'middle', letterSpacing: 0.5
                }}>INACTIVE</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#aab', fontSize: 18, width: 34, height: 34,
              borderRadius: 8, cursor: 'pointer', fontWeight: 700
            }}
            title="Close (Esc)"
          >×</button>
        </div>

        {/* Tabs */}
        {me && userId !== me.id && (
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            padding: '0 22px', background: 'rgba(0,0,0,0.25)'
          }}>
            <TabBtn active={tab === 'stats'} onClick={() => setTab('stats')}>Stats</TabBtn>
            <TabBtn active={tab === 'messages'} onClick={() => setTab('messages')}>
              💬 Messages
            </TabBtn>
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '18px 22px' }}>
          {tab === 'messages' && me && userId !== me.id && (
            <DMChat otherUserId={userId} me={me} height={300} />
          )}
          {tab === 'stats' && loading && (
            <div style={{ color: '#778', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>
              Loading stats…
            </div>
          )}
          {tab === 'stats' && err && (
            <div style={{ color: '#ff8888', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              {err}
            </div>
          )}

          {tab === 'stats' && data && !loading && (
            <>
              {/* Season W/L block */}
              <Section title="Season Record">
                {data.allTime.correct + data.allTime.wrong === 0 ? (
                  <div style={{ color: '#667', fontSize: 13 }}>No finalized picks yet.</div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                      <StatBlock label="Correct" value={data.allTime.correct} color="#4caf50" />
                      <StatBlock label="Wrong"   value={data.allTime.wrong}   color="#ef5350" />
                      <StatBlock
                        label="Win %"
                        value={data.allTime.winPct != null ? `${data.allTime.winPct}%` : '-'}
                        color={pctColor(data.allTime.winPct)}
                      />
                    </div>
                    {data.perSeason.length > 1 && (
                      <div style={{ fontSize: 11, color: '#667', fontWeight: 700, letterSpacing: 0.5, margin: '8px 0 4px' }}>
                        BY SEASON
                      </div>
                    )}
                    {data.perSeason.length > 1 && data.perSeason.map(season => (
                      <div key={season.season} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '6px 0', borderTop: '1px dashed rgba(255,255,255,0.05)',
                        fontSize: 13, color: '#aab'
                      }}>
                        <span style={{ fontWeight: 700 }}>{season.season}</span>
                        <span>
                          <span style={{ color: '#4caf50' }}>{season.correct}W</span>
                          <span style={{ color: '#556', margin: '0 5px' }}>-</span>
                          <span style={{ color: '#ef5350' }}>{season.wrong}L</span>
                          {season.winPct != null && (
                            <span style={{ color: pctColor(season.winPct), marginLeft: 10, fontWeight: 700 }}>{season.winPct}%</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </Section>

              {/* Badges */}
              <Section title="Badges">
                {/* The lists carry preseason badges too, and the counts beside
                    them are season-only, so testing the counts alone announced
                    "no badges earned yet" over a list that had some in it. */}
                {(data.badges.perfectWeeks || []).length === 0 && (data.badges.poolWins || []).length === 0 ? (
                  <div style={{ color: '#667', fontSize: 13 }}>No badges earned yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(data.badges.perfectWeeks || []).filter(w => !w.preseason).length > 0 && (
                      <div>
                        <div style={{ color: '#FFD700', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                          ⭐ Perfect Weeks ({data.badges.summary.perfectWeeks})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {data.badges.perfectWeeks.filter(w => !w.preseason).map(w => (
                            <span key={w.week_id} style={{
                              fontSize: 11, padding: '3px 9px', borderRadius: 10,
                              background: 'rgba(255,215,0,0.12)',
                              border: '1px solid rgba(255,215,0,0.3)',
                              color: '#FFD700', fontWeight: 600
                            }}>{w.label} ({w.correct}/{w.total})</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(data.badges.perfectWeeks || []).filter(w => w.preseason).length > 0 && (
                      <div>
                        <div style={{ color: '#99a', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                          ⭐ Perfect Weeks, preseason ({data.badges.summary.preseasonPerfectWeeks})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {data.badges.perfectWeeks.filter(w => w.preseason).map(w => (
                            <span key={w.week_id} style={{
                              fontSize: 11, padding: '3px 9px', borderRadius: 10,
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.14)',
                              color: '#99a', fontWeight: 600
                            }}>{w.label} ({w.correct}/{w.total})</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(data.badges.poolWins || []).length > 0 && (
                      <div>
                        <div style={{ color: '#ff8b8b', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                          🏆 Weekly Pool Wins ({data.badges.summary.poolWins})
                          {data.badges.summary.preseasonPoolWins > 0 && (
                            <span style={{ color: '#667', fontWeight: 600 }}>
                              {' '}plus {data.badges.summary.preseasonPoolWins} in preseason
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {data.badges.poolWins.map((p, i) => (
                            <div key={i} style={{ fontSize: 12, color: '#aab' }}>
                              <span style={{ color: '#ff8b8b', fontWeight: 700 }}>{p.pool_name}</span>
                              <span style={{ color: '#556' }}> · </span>
                              {p.label}
                              {p.preseason && (
                                <span style={{ color: '#667', marginLeft: 6, fontSize: 11 }}>preseason</span>
                              )}
                              {p.tied && <span style={{ color: '#ffa726', marginLeft: 6, fontSize: 11 }}>(tied)</span>}
                              {p.correct != null && (
                                <span style={{ color: '#667', marginLeft: 6, fontSize: 11 }}>• {p.correct} correct</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Section>

              {/* Shared pools */}
              {data.sharedPools?.length > 0 && (
                <Section title={`Shared Pools (${data.sharedPools.length})`}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.sharedPools.map(p => (
                      <span key={p.id} style={{
                        fontSize: 12, padding: '4px 10px', borderRadius: 14,
                        background: 'rgba(127,179,255,0.12)',
                        border: '1px solid rgba(127,179,255,0.25)',
                        color: '#7fb3ff', fontWeight: 600
                      }}>{p.name}</span>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontFamily: "'Oswald', sans-serif",
        fontSize: 12, color: '#7fb3ff', letterSpacing: 1.5,
        textTransform: 'uppercase', fontWeight: 700, marginBottom: 8
      }}>{title}</div>
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 10, padding: 12
      }}>{children}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: '12px 16px',
        fontFamily: "'Oswald', sans-serif",
        fontSize: 13,
        letterSpacing: 1,
        fontWeight: 700,
        color: active ? '#7fb3ff' : '#667',
        borderBottom: active ? '2px solid #7fb3ff' : '2px solid transparent',
        cursor: 'pointer',
        textTransform: 'uppercase'
      }}
    >
      {children}
    </button>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 8, padding: '10px 8px', textAlign: 'center'
    }}>
      <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: '#667', marginTop: 2, letterSpacing: 0.5, fontWeight: 600 }}>{label.toUpperCase()}</div>
    </div>
  );
}
